import { createGroundElevation } from '../../site/groundElevation';
import { elevatedBuilder, elevatedDetails } from '../mesh/elevatedBuilder';
import { footedBuilder } from '../mesh/footedBuilder';
import type { BuildContext, FeatureOutput, WorldFeature, Obstacle } from '../types';
import type { Rng } from '../../core/rng';
import type { Lot } from './types';
import { footDepth, houseFloorHeight, stoepFoot } from './foundation';
import { lotFrame } from './lotFrame';
import { STOEP_REACH_MOST } from './stoep';
import { buildHouseOnLot } from './house';
import { buildAllLandmarks } from './landmarks/landmarkFeature';
import { lifeDoorways } from '../life/crowd/crowdDoorways';
import { buildFortifications } from './fortifications/fortificationFeature';
import { buildCityTrees } from './fabricTrees';
import { buildHogeHuisYard, hogeHuisTreeObstacles } from './hogeHuisYard';
import { buildRaamEnclosures } from './raamEnclosures';
import type { PlannedLots } from './fabricLots';
import { partyWallsOf, type PartyWalls } from './partyWalls';
import { createDetailCollector, type DetailCollector, type DetailInstances } from './detailSink';
import { buildDetailTemplates } from './openingDetail';
import { buildOpeningLod, BACK_LOD1, LOD1, type OpeningLod } from './openingLod';
import { lotKey } from './lotKey';
import { houseStreetsOf } from './houseStreets';
import { blockKeysOf } from './painted/blockSides';
import { paintedRoofMaterials } from './painted/paintedRoofs';
import { woodcutGables } from './woodcut/woodcutGables';
import type { NearDetailSet } from '../mesh/nearDetailLod';
import { buildLandmarkTracery } from './landmarks/tracery/traceryLod';
import type { TraceryKind } from './landmarks/tracery/traceryLayout';

function openingCounts(sets: readonly DetailInstances[]): Record<string, number> {
  const of = (kind: DetailInstances['kind']) =>
    sets.filter((s) => s.kind === kind).reduce((n, s) => n + s.count, 0);
  return {
    windows: of('window'),
    doors: of('doorLeaf'),
    loftDoors: of('loftDoor'),
    smallWindows: of('smallWindow'),
    shops: of('shop'),
  };
}

/* The houses' and landmarks' joinery, and the back buildings', each shown within its own reach. */
function joineryLods(
  ctx: BuildContext,
  rng: Rng,
  houses: readonly DetailInstances[],
  backs: readonly DetailInstances[]
): readonly OpeningLod[] {
  const material = ctx.materials.painted({ instanced: true });
  /* Each LOD files its own instance ids on its templates, so the two cannot share them. */
  const templates = () => buildDetailTemplates(ctx.palette, rng.fork('openingDetails'));
  return [
    buildOpeningLod(houses, templates(), material, {
      name: 'opening-details',
      radius: LOD1.radius,
    }),
    buildOpeningLod(backs, templates(), material, {
      name: 'back-opening-details',
      radius: BACK_LOD1.radius,
    }),
  ];
}

function lotCounts({ houses, backhouses, gangen }: PlannedLots): Record<string, number> {
  return {
    lots: houses.length + backhouses.length,
    houses: houses.length,
    backhouses: backhouses.length,
    gangen: gangen.length,
    kamers: gangen.reduce((n, gang) => n + gang.kamers.length, 0),
  };
}

/* Every building on a lot, into the chunk at its lot, its joinery to the houses' or the back buildings' sink. */
export function buildLotHouses(
  ctx: BuildContext,
  rng: Rng,
  lots: readonly Lot[],
  sinks: { readonly houses: DetailCollector; readonly backs: DetailCollector }
): {
  readonly triangles: number;
  readonly obstacles: readonly Obstacle[];
  readonly party: readonly PartyWalls[];
} {
  const prepared = prepareLotHouses(ctx, rng, lots, sinks);
  return { ...prepared.build(0, lots.length), party: prepared.party };
}

/** Keep neighbourhood-wide decisions fixed while building a bounded batch of houses. */
function prepareLotHouses(
  ctx: BuildContext,
  rng: Rng,
  lots: readonly Lot[],
  sinks: { readonly houses: DetailCollector; readonly backs: DetailCollector }
) {
  const elevation = createGroundElevation(ctx.layout);
  const party = partyWallsOf(lots);
  const streets = houseStreetsOf(lots, party, ctx.layout);
  const keys = blockKeysOf(lots, streets);
  const roofs = paintedRoofMaterials(lots, keys, rng);
  const gables = woodcutGables(lots, keys, rng);
  const activeDoors = new Set(
    lifeDoorways(ctx.lifePlan?.entrances ?? [], ctx.citizens ? ctx.lifePlan?.crowd : undefined).map(
      (entry) => entry.lotKey
    )
  );
  return {
    party,
    build(from: number, to: number) {
      let triangles = 0;
      const obstacles = lots.slice(from, to).map((lot, offset): Obstacle => {
        const i = from + offset;
        const front = lotFrame(lot);
        const height = houseFloorHeight(front, elevation);
        const raw = ctx.chunks.builderAt('fabric', lot.obb.center);
        const builder = elevatedBuilder(
          footedBuilder(raw, height, footDepth([lot.corners], elevation, height)),
          height
        );
        const sink = elevatedDetails(lot.kind === 'backhouse' ? sinks.backs : sinks.houses, height);
        triangles += buildHouseOnLot(
          builder,
          lot,
          ctx.palette,
          rng.fork(lotKey(lot)),
          party[i]!,
          sink,
          streets[i],
          { roof: roofs[i] ?? null, gable: gables[i] ?? null },
          activeDoors.has(lotKey(lot)),
          stoepFoot(front, height, STOEP_REACH_MOST, elevation)
        ).triangleCount;
        return { id: `lot:${lot.index}`, kind: 'house', polygon: [...lot.corners] };
      });
      return { triangles, obstacles };
    },
  };
}

/* The landmarks, their joinery with the houses', their window tracery, and the fortifications. */
function buildCivic(
  ctx: BuildContext,
  rng: Rng,
  details: DetailCollector
): {
  readonly triangles: number;
  readonly obstacles: readonly Obstacle[];
  readonly counts: Record<string, number>;
  readonly tracery: readonly NearDetailSet<TraceryKind>[];
} {
  const warn = (msg: string): void => {
    ctx.warn(msg);
  };
  const landmarks = buildAllLandmarks(
    ctx.layout.landmarks,
    ctx.chunks,
    ctx.palette,
    rng.fork('landmarks'),
    {
      elevationAt: createGroundElevation(ctx.layout),
      marketGoods: false,
      squares: ctx.layout.squares.map((square) => square.polygon),
      details,
      warn,
    }
  );
  const forts = buildFortifications(
    ctx.layout,
    ctx.chunks,
    ctx.palette,
    rng.fork('fortifications'),
    warn,
    createGroundElevation(ctx.layout)
  );
  const gardens = buildRaamEnclosures(ctx);
  const timber = buildHogeHuisYard(ctx);
  return {
    triangles: landmarks.triangles + forts.triangles + gardens.triangles + timber.triangles,
    obstacles: [
      ...landmarks.obstacles,
      ...forts.obstacles,
      ...gardens.obstacles,
      ...timber.obstacles,
    ],
    counts: {
      landmarks: ctx.layout.landmarks.length,
      walls: forts.wallCount,
      towers: forts.towerCount,
      gates: forts.gateCount,
      gardenFenceRuns: gardens.runs,
      gardenEntrances: gardens.entrances,
      hogeHuisTimberStacks: timber.stacks,
    },
    tracery: landmarks.tracery,
  };
}

function partyCounts(party: readonly PartyWalls[]): Record<string, number> {
  const shared = party.reduce((n, p) => n + Number(p.left) + Number(p.right), 0);
  return { partyWalls: shared, exposedSideWalls: party.length * 2 - shared };
}

export const fabricFeature: WorldFeature = {
  id: 'fabric',
  label: 'City fabric',
  // Four of the fabric's former 25 went to the lot plan, which buildWorld now makes (lotPlan.ts).
  weight: 21,

  batches(ctx: BuildContext) {
    const rng = ctx.rng.fork('fabric');
    const planned = ctx.lots;
    const lots = [...planned.houses, ...planned.backhouses];
    const details = createDetailCollector(),
      backs = createDetailCollector();
    const prepared = prepareLotHouses(ctx, rng, lots, { houses: details, backs });
    const obstacles: Obstacle[] = [];
    let civic: ReturnType<typeof buildCivic> | undefined;
    const size = 128,
      count = Math.ceil(lots.length / size);
    return [
      ...Array.from({ length: count }, (_, i) => ({
        label: `Buildings ${i + 1}/${count}`,
        build: (): FeatureOutput => {
          const batch = prepared.build(i * size, (i + 1) * size);
          obstacles.push(...batch.obstacles);
          return {
            objects: [],
            animated: [],
            obstacles: batch.obstacles,
            counts: {
              triangles: batch.triangles,
              ...(i === 0 ? { ...lotCounts(planned), ...partyCounts(prepared.party) } : {}),
            },
          };
        },
      })),
      {
        label: 'Civic buildings and defences',
        build: (): FeatureOutput => {
          civic = buildCivic(ctx, rng, details);
          obstacles.push(...civic.obstacles);
          return {
            objects: [],
            animated: [],
            obstacles: civic.obstacles,
            counts: { ...civic.counts, triangles: civic.triangles },
          };
        },
      },
      {
        label: 'Building details',
        build: (): FeatureOutput => {
          if (!civic) throw new Error('Civic batch must precede building details');
          const trees = buildCityTrees(
            ctx,
            [...obstacles, ...hogeHuisTreeObstacles(ctx)],
            rng,
            lots.length
          );
          const houseSets = details.finish(),
            backSets = backs.finish();
          ctx.roofs?.push(...details.roofs, ...backs.roofs);
          const lods = joineryLods(ctx, rng, houseSets, backSets);
          const tracery = buildLandmarkTracery(
            civic.tracery,
            ctx.palette,
            ctx.materials.painted({ instanced: true }),
            rng.fork('landmarkTracery')
          );
          return {
            objects: [...trees.meshes, ...lods.map((lod) => lod.root), tracery.root],
            animated: [],
            obstacles: [],
            counts: { yardTrees: trees.yardTrees, ...openingCounts([...houseSets, ...backSets]) },
          };
        },
      },
    ];
  },

  build(ctx: BuildContext): FeatureOutput {
    const featureRng = ctx.rng.fork('fabric');
    const planned = ctx.lots;
    const allLots = [...planned.houses, ...planned.backhouses];
    const details = createDetailCollector();
    const backDetails = createDetailCollector();
    const lotHouses = buildLotHouses(ctx, featureRng, allLots, {
      houses: details,
      backs: backDetails,
    });
    const civic = buildCivic(ctx, featureRng, details);
    const obstacles = [...lotHouses.obstacles, ...civic.obstacles];
    const cityTrees = buildCityTrees(
      ctx,
      [...obstacles, ...hogeHuisTreeObstacles(ctx)],
      featureRng,
      allLots.length
    );
    const houseSets = details.finish();
    const backSets = backDetails.finish();
    ctx.roofs?.push(...details.roofs, ...backDetails.roofs);
    const lods = joineryLods(ctx, featureRng, houseSets, backSets);
    const tracery = buildLandmarkTracery(
      civic.tracery,
      ctx.palette,
      ctx.materials.painted({ instanced: true }),
      featureRng.fork('landmarkTracery')
    );

    return {
      objects: [...cityTrees.meshes, ...lods.map((lod) => lod.root), tracery.root],
      obstacles,
      animated: [],
      counts: {
        ...lotCounts(planned),
        ...civic.counts,
        yardTrees: cityTrees.yardTrees,
        ...openingCounts([...houseSets, ...backSets]),
        ...partyCounts(lotHouses.party),
        triangles: lotHouses.triangles + civic.triangles,
      },
    };
  },
};
